import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from "react-i18next";
import { TreeSelectComponent } from '../../Components/TreeSelectComponent';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import CustomToast from '../../Components/CustomToast';
import { GlobalSearch } from '../../Components/GlobalSearch';
import { Tooltip } from 'primereact/tooltip';
import { amountDisplayPerPolicy, formatBillingPeriodText, policyDisplay } from '../../Components/CleanInputs';
import { useDispatch, useSelector } from 'react-redux';
import PaymentScheduleActions from "../../Redux/transition/payment-schedule/payment-schedule.reducer";
import BankActions from "../../Redux/actions/bank-actions";
import BusinessActions from "../../Redux/actions/business-actions";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import '../../Styles/components/reactPhoneNumberInput.scss';
import { classNames } from 'primereact/utils';
import { normalizePhoneNumber, toLocalPhoneNumber, isValidInternationalPhoneNumber } from '../../utils/phoneUtils';
import {
    buildMomoPaymentFormData,
    resolveLinkedSystemBank,
} from '../../utils/momoPaymentUtils';
import AutoBillingBadge from '../../Components/AutoBillingBadge';

const MOMO_WAITING_MODAL_MIN_MS = 3000;

const CollectPayment = (props) => {
    const { state, setState, countryCurrency, membershipId, callBack, defMembership } = props;

    const { t } = useTranslation("common");
    const collectPaymentRef = useRef(null);
    const pollingStartTimeRef = useRef(null);
    const waitingHoldTimerRef = useRef(null);
    const pendingMomoOutcomeRef = useRef(null);
    const hadPollingPhaseRef = useRef(false);
    const momoOutcomeHandledRef = useRef(false);
    const paymentFlowActiveRef = useRef(false);
    const dispatch = useDispatch();

    const defaultCountry = defMembership?.business?.country?.code?.toLowerCase() || 'gh';
    const defaultDialCode = defMembership?.business?.country?.phoneCode?.replace(/\D/g, '') || '';

    const [inputAmount, setInputAmount] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [dialCode, setDialCode] = useState(defaultDialCode);
    const [countryCode, setCountryCode] = useState(defMembership?.business?.country?.code || 'GH');
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [systemBank, setSystemBank] = useState(null);
    const [showErr, setShowErr] = useState(false);
    const [toastTrigger, setToastTrigger] = useState(false);
    const [collectClicked, setCollectClicked] = useState(false);
    const [toastData, setToastData] = useState({ summary: '', severity: '', detail: '', life: 0 });
    const [showWaitingModal, setShowWaitingModal] = useState(false);
    const [bcId, setBcId] = useState(null);
    const [collectState, setCollectState] = useState({
        filteredCustomers: [],
        exactFilteredBusinessCustomers: [],
        selectedCustomerId: null,
        isCustomerSelected: false,
        verificationStatus: null,
        fetchingVerifRecord: true,
        businessCustomerRecord: {},
        query: ""
    });

    const linkedBanks = useSelector((bankState) => bankState.banks.linkedBanks);
    const fetchingLinkedBanks = useSelector((bankState) => bankState.banks.fetchingLinkedBanks);
    const business = useSelector((businessState) => businessState.businesses.business);
    const paymentCredentials = useSelector((payswitchState) => payswitchState.payswitch.credentials);
    const paymentProvider = business?.pmtProvider
        ?? defMembership?.business?.pmtProvider
        ?? paymentCredentials?.provider;
    const {
        triggeringMomoPaymentByPlan,
        triggeringMomoPaymentBySchedule,
        triggeringMomoPaymentBySubscriber,
        pollingMomoPaymentByPlan,
        pollingMomoPaymentBySchedule,
        pollingMomoPaymentBySubscriber,
        triggerMomoPaymentByPlan,
        triggerMomoPaymentBySchedule,
        triggerMomoPaymentBySubscriber,
        errorTriggeringMomoPaymentByPlan,
        errorTriggeringMomoPaymentBySchedule,
        errorTriggeringMomoPaymentBySubscriber,
    } = useSelector((paymentState) => paymentState.paymentSchedules);

    const isTriggeringMomoPayment = triggeringMomoPaymentByPlan
        || triggeringMomoPaymentBySchedule
        || triggeringMomoPaymentBySubscriber;

    const isPollingMomoPayment = pollingMomoPaymentByPlan
        || pollingMomoPaymentBySchedule
        || pollingMomoPaymentBySubscriber;

    const triggerMomoByPlan = useCallback(
        (planId, businessCustomerId, memberId, businessId, provider, formData) => {
            dispatch(PaymentScheduleActions.triggerMomoPaymentByPlanRequest(
                planId, businessCustomerId, memberId, businessId, provider, formData
            ));
        },
        [dispatch]
    );

    const triggerMomoBySchedule = useCallback(
        (scheduleId, businessCustomerId, memberId, businessId, provider, formData) => {
            dispatch(PaymentScheduleActions.triggerMomoPaymentByScheduleRequest(
                scheduleId, businessCustomerId, memberId, businessId, provider, formData
            ));
        },
        [dispatch]
    );

    const triggerMomoBySubscriber = useCallback(
        (subscriberId, businessCustomerId, memberId, businessId, provider, formData) => {
            dispatch(PaymentScheduleActions.triggerMomoPaymentBySubscriberRequest(
                subscriberId, businessCustomerId, memberId, businessId, provider, formData
            ));
        },
        [dispatch]
    );

    const getLinkedBanks = useCallback(
        (provider, type) => dispatch(BankActions.linkedBanksRequest(provider, type)),
        [dispatch]
    );

    const getBusiness = useCallback(
        (businessId) => dispatch(BusinessActions.businessRequest(businessId)),
        [dispatch]
    );

    const selectedPlan = state.treePlanSelected || state.plan || state.currentSubscription?.subscriber?.plan;
    const showAmountField = selectedPlan && ["FIXED", "FLEXIBLE"].includes(selectedPlan.policy);
    const mobileBanks = useMemo(() => {
        if (!linkedBanks) {
            return [];
        }

        const linkedBankList = Array.isArray(linkedBanks) ? linkedBanks : [];
        return linkedBankList.map(resolveLinkedSystemBank).filter((bank) => bank?.name);
    }, [linkedBanks]);

    const validatePhone = useCallback((phone, nextDialCode, nextCountryCode) => {
        if (!phone || phone.trim() === '') {
            setPhoneError(t("invitation.number_error"));
            return false;
        }

        if (!isValidInternationalPhoneNumber(phone, nextDialCode, nextCountryCode)) {
            setPhoneError(t("invitation.invalid_number"));
            return false;
        }

        setPhoneError('');
        return true;
    }, [t]);

    const requiresTreePlanSelect = !state.plan && !state.currentSubscription && !state.paymentScheduleId;
    const requiresCustomerSearch = !!state.plan;
    const requiresAmount = showAmountField;

    const hasRequiredTreePlan = !requiresTreePlanSelect || !!state.treePlanSelected;
    const hasRequiredCustomer = !requiresCustomerSearch || !!bcId;
    const hasRequiredSubscription = !state.onSubscriptionsTable || !!state.currentSubscription;
    const hasRequiredAmount = !requiresAmount || (!!inputAmount && !showErr);
    const hasRequiredNetwork = !!systemBank;
    const hasRequiredPhone = !!phoneNumber
        && isValidInternationalPhoneNumber(phoneNumber, dialCode, countryCode)
        && !phoneError;

    const getMomoPaymentErrorDetail = useCallback((error) => {
        if (!error) {
            return t("plan.collect_failure");
        }
        if (error.detail || error.message || error.title) {
            return error.detail || error.message || error.title;
        }
        if (error.messageKey) {
            return t(error.messageKey);
        }
        if (typeof error === "string") {
            return error;
        }
        return t("plan.collect_failure");
    }, [t]);

    const showToast = useCallback((summary = "", severity = "", detail = "", life = 3000) => {
        setToastData({ summary, severity, detail, life });
        setToastTrigger(true);
    }, []);

    const resetWaitingModalState = useCallback(() => {
        if (waitingHoldTimerRef.current) {
            clearTimeout(waitingHoldTimerRef.current);
            waitingHoldTimerRef.current = null;
        }
        pollingStartTimeRef.current = null;
        pendingMomoOutcomeRef.current = null;
        hadPollingPhaseRef.current = false;
        setShowWaitingModal(false);
    }, []);

    const finishMomoPaymentFlow = useCallback(() => {
        paymentFlowActiveRef.current = false;
        setCollectClicked(false);
        momoOutcomeHandledRef.current = false;
        hadPollingPhaseRef.current = false;
    }, []);

    const finishWaitingModalHold = useCallback(() => {
        if (waitingHoldTimerRef.current) {
            clearTimeout(waitingHoldTimerRef.current);
            waitingHoldTimerRef.current = null;
        }

        const pending = pendingMomoOutcomeRef.current;
        pendingMomoOutcomeRef.current = null;
        pollingStartTimeRef.current = null;
        hadPollingPhaseRef.current = false;

        if (pending?.type === 'success') {
            showToast(t("success"), "success", t("plan.success_collect"));
        } else if (pending?.type === 'error') {
            showToast(t("err"), "error", getMomoPaymentErrorDetail(pending.error), 5000);
        }

        paymentFlowActiveRef.current = false;
        setShowWaitingModal(false);
    }, [getMomoPaymentErrorDetail, showToast, t]);

    const finishWaitingModalHoldRef = useRef(finishWaitingModalHold);
    finishWaitingModalHoldRef.current = finishWaitingModalHold;

    const scheduleWaitingModalHoldCompletion = useCallback(() => {
        if (waitingHoldTimerRef.current) {
            clearTimeout(waitingHoldTimerRef.current);
            waitingHoldTimerRef.current = null;
        }

        if (!pollingStartTimeRef.current) {
            finishWaitingModalHoldRef.current();
            return;
        }

        const elapsed = Date.now() - pollingStartTimeRef.current;
        const remaining = Math.max(0, MOMO_WAITING_MODAL_MIN_MS - elapsed);

        waitingHoldTimerRef.current = setTimeout(() => {
            finishWaitingModalHoldRef.current();
        }, remaining);
    }, []);

    const finalizeMomoPaymentOutcome = useCallback((outcome) => {
        if (momoOutcomeHandledRef.current) {
            return;
        }

        momoOutcomeHandledRef.current = true;
        pendingMomoOutcomeRef.current = outcome;
        scheduleWaitingModalHoldCompletion();
    }, [scheduleWaitingModalHoldCompletion]);

    const resetNonPlanCollectState = useCallback(() => {
        setState((prev) => {
            if (prev.plan) {
                return prev;
            }

            return {
                ...prev,
                showCollectPopup: false,
                currentSubscriber: null,
                currentSubscription: null,
                treePlanSelectedKey: null,
                treePlanSelected: null,
                oneOffOnly: false,
                onSubscriptionsTable: false,
                paymentScheduleId: null,
            };
        });
    }, [setState]);

    const disableButtonConditions = isTriggeringMomoPayment
        || isPollingMomoPayment
        || showWaitingModal
        || collectClicked
        || !paymentProvider
        || !hasRequiredTreePlan
        || !hasRequiredCustomer
        || !hasRequiredSubscription
        || !hasRequiredAmount
        || !hasRequiredNetwork
        || !hasRequiredPhone;

    useEffect(() => {
        if (!state.showCollectPopup || !defMembership?.business?.id || paymentProvider) {
            return;
        }

        getBusiness(defMembership.business.id);
    }, [state.showCollectPopup, defMembership, paymentProvider, getBusiness]);

    useEffect(() => {
        if (state.showCollectPopup && paymentProvider) {
            getLinkedBanks(paymentProvider, "MOBILE");
        }
    }, [state.showCollectPopup, paymentProvider, getLinkedBanks]);

    useEffect(() => {
        if (!systemBank || !mobileBanks.length) {
            return;
        }

        const isSelectedBankSupported = mobileBanks.some((bank) => bank.id === systemBank.id);
        if (!isSelectedBankSupported) {
            setSystemBank(null);
        }
    }, [mobileBanks, systemBank]);

    useEffect(() => {
        if (collectState?.businessCustomerRecord) {
            setBcId(collectState.businessCustomerRecord.id);
        }
    }, [collectState.businessCustomerRecord]);

    useEffect(() => {
        if (isTriggeringMomoPayment || isPollingMomoPayment) {
            hadPollingPhaseRef.current = true;
        }
    }, [isTriggeringMomoPayment, isPollingMomoPayment]);

    useEffect(() => () => resetWaitingModalState(), [resetWaitingModalState]);

    useEffect(() => {
        if (!paymentFlowActiveRef.current || momoOutcomeHandledRef.current) {
            return;
        }

        const momoPaymentSucceeded = (!triggeringMomoPaymentByPlan && !pollingMomoPaymentByPlan && triggerMomoPaymentByPlan)
            || (!triggeringMomoPaymentBySchedule && !pollingMomoPaymentBySchedule && triggerMomoPaymentBySchedule)
            || (!triggeringMomoPaymentBySubscriber && !pollingMomoPaymentBySubscriber && triggerMomoPaymentBySubscriber);

        const momoPaymentFailed = (!triggeringMomoPaymentByPlan && !pollingMomoPaymentByPlan && errorTriggeringMomoPaymentByPlan)
            || (!triggeringMomoPaymentBySchedule && !pollingMomoPaymentBySchedule && errorTriggeringMomoPaymentBySchedule)
            || (!triggeringMomoPaymentBySubscriber && !pollingMomoPaymentBySubscriber && errorTriggeringMomoPaymentBySubscriber);

        if (momoPaymentSucceeded) {
            finalizeMomoPaymentOutcome({ type: 'success' });
            return;
        }

        if (momoPaymentFailed) {
            const error = errorTriggeringMomoPaymentByPlan
                ?? errorTriggeringMomoPaymentBySchedule
                ?? errorTriggeringMomoPaymentBySubscriber;
            finalizeMomoPaymentOutcome({ type: 'error', error });
        }
    }, [
        triggeringMomoPaymentByPlan,
        triggeringMomoPaymentBySchedule,
        triggeringMomoPaymentBySubscriber,
        pollingMomoPaymentByPlan,
        pollingMomoPaymentBySchedule,
        pollingMomoPaymentBySubscriber,
        triggerMomoPaymentByPlan,
        triggerMomoPaymentBySchedule,
        triggerMomoPaymentBySubscriber,
        errorTriggeringMomoPaymentByPlan,
        errorTriggeringMomoPaymentBySchedule,
        errorTriggeringMomoPaymentBySubscriber,
        finalizeMomoPaymentOutcome,
    ]);

    const clearFields = () => {
        setCollectState(prev => ({ ...prev, query: "" }));
        setInputAmount('');
        setPhoneNumber('');
        setDialCode(defaultDialCode);
        setCountryCode(defMembership?.business?.country?.code || 'GH');
        setPhoneTouched(false);
        setPhoneError('');
        setSystemBank(null);
        setBcId(null);
        resetWaitingModalState();
    };

    function hidePopup() {
        if (paymentFlowActiveRef.current || isPollingMomoPayment || isTriggeringMomoPayment || showWaitingModal) {
            return;
        }

        resetWaitingModalState();
        finishMomoPaymentFlow();
        setState(prev => ({
            ...prev,
            showCollectPopup: false,
            currentSubscriber: null,
            currentSubscription: null,
            treePlanSelectedKey: null,
            treePlanSelected: null,
            oneOffOnly: false,
            onSubscriptionsTable: false,
            paymentScheduleId: null,
        }));
        clearFields();
        if (state.plan) {
            callBack?.();
        }
    }

    const handleAmtChange = (e) => {
        let val = e?.target.value;
        val = val.replace(/[^0-9.]/g, '');

        if (val === '') {
            setInputAmount('');
            setShowErr(false);
            return;
        }

        val = val.replace(/(\..*?)\..*/g, '$1');
        const match = val.match(/^\d*(\.?\d{0,2})/);
        const cleanValue = match ? match[0] : '';

        const numericValue = parseFloat(cleanValue);
        const minAmount = 1;
        const maxAmount = 10000;

        if (numericValue < minAmount || numericValue > maxAmount) {
            setInputAmount(cleanValue);
            setShowErr(true);
        } else {
            setInputAmount(cleanValue);
            setShowErr(false);
        }
    };

    const handlePhoneChange = (phone, { country } = {}) => {
        const nextDialCode = country?.dialCode || dialCode;
        const nextCountryCode = (country?.iso2 || defaultCountry || '').toUpperCase();

        setPhoneNumber(phone);
        setDialCode(nextDialCode);
        setCountryCode(nextCountryCode);
        validatePhone(phone, nextDialCode, nextCountryCode);
    };

    const handlePhoneBlur = () => {
        setPhoneTouched(true);
        validatePhone(phoneNumber, dialCode, countryCode);
    };

    const onSubmit = () => {
        setPhoneTouched(true);
        if (!validatePhone(phoneNumber, dialCode, countryCode)) {
            return;
        }

        const businessCustomerId = state.currentSubscription?.businessCustomer?.id
            ?? state.currentSubscriber?.businessCustomer?.id
            ?? state.customerRecord?.id
            ?? bcId;
        const businessId = defMembership?.business?.id;

        if (!businessId || !paymentProvider) {
            showToast(t("err"), "error", t("plan.collect_failure"), 5000);
            return;
        }

        const normalizedPhone = normalizePhoneNumber(phoneNumber, dialCode, countryCode);
        const formattedIdentifier = toLocalPhoneNumber(normalizedPhone, dialCode, countryCode);
        const paymentAmount = showAmountField && inputAmount
            ? parseFloat(inputAmount)
            : selectedPlan?.cost != null
                ? parseFloat(selectedPlan.cost)
                : parseFloat(inputAmount);

        const formData = buildMomoPaymentFormData({
            systemBank,
            identifier: formattedIdentifier,
            amount: paymentAmount,
        });

        setCollectClicked(true);
        momoOutcomeHandledRef.current = false;
        paymentFlowActiveRef.current = true;
        setShowWaitingModal(true);
        pendingMomoOutcomeRef.current = null;
        hadPollingPhaseRef.current = false;
        pollingStartTimeRef.current = Date.now();
        if (waitingHoldTimerRef.current) {
            clearTimeout(waitingHoldTimerRef.current);
            waitingHoldTimerRef.current = null;
        }

        if (!state.plan) {
            setState((prev) => ({ ...prev, showCollectPopup: false }));
        }

        if (state.paymentScheduleId) {
            triggerMomoBySchedule(state.paymentScheduleId, businessCustomerId, membershipId, businessId, paymentProvider, formData);
        } else if (state.onSubscriptionsTable && state.currentSubscription?.id) {
            triggerMomoBySubscriber(state.currentSubscription.id, businessCustomerId, membershipId, businessId, paymentProvider, formData);
        } else if (state.plan?.id) {
            triggerMomoByPlan(state.plan.id, businessCustomerId, membershipId, businessId, paymentProvider, formData);
        } else {
            triggerMomoByPlan(state.treePlanSelected?.id, businessCustomerId, membershipId, businessId, paymentProvider, formData);
        }
    };

    const subscriberName = state.currentSubscription?.businessCustomer?.name
        ?? state.currentSubscriber?.businessCustomer?.name
        ?? state.customerRecord?.name;

    const isPaymentFlowActive = isTriggeringMomoPayment || isPollingMomoPayment || showWaitingModal;

    return (
        <div>
            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                handleActionsAfterToast={() => {
                    setToastTrigger(false);
                    finishMomoPaymentFlow();
                    clearFields();
                    if (state.plan) {
                        return;
                    }
                    resetNonPlanCollectState();
                    callBack?.();
                }}
                handleActionsAfterErrorToast={() => {
                    setToastTrigger(false);
                    finishMomoPaymentFlow();
                    resetWaitingModalState();
                }}
            />

            <Dialog
                visible={state.showCollectPopup}
                header={t("business_customers.collect_payment_title")}
                style={{ width: "350px" }}
                modal={!showWaitingModal}
                closable={!isPaymentFlowActive}
                onHide={() => {
                    if (paymentFlowActiveRef.current) {
                        return;
                    }
                    hidePopup();
                }}
            >
                <section className='p-3'>
                    {state.plan ? (
                        <div>
                            <div className='flex align-items-center gap-2'>
                                <p className='mb-0 reveknew_gray'>{state.plan?.name}</p>
                                <AutoBillingBadge collectionMode={state.plan?.collectionMode} />
                            </div>
                            {state.plan?.description && (
                                <p className='mb-1 reveknew_black text-sm'>{state.plan?.description}</p>
                            )}
                            <div className='flex gap-2'>
                                <p className='mb-0'>{policyDisplay(state.plan)}</p>
                                <p className='mb-0 account_name'>
                                    {amountDisplayPerPolicy(defMembership?.business?.currency, state.plan)}
                                </p>
                                <p className='mb-0 reveknew_red'>{formatBillingPeriodText(state.plan?.billingPeriod)}</p>
                            </div>
                        </div>
                    ) : state.currentSubscription ? (
                        <div>
                            <div className='flex align-items-center gap-2'>
                                <p className='mb-0 reveknew_gray'>{state.currentSubscription?.subscriber?.plan.name}</p>
                                <AutoBillingBadge collectionMode={state.currentSubscription?.subscriber?.plan.collectionMode} />
                            </div>
                            {state.currentSubscription?.subscriber?.plan.description && (
                                <p className='mb-1 reveknew_black text-sm'>{state.currentSubscription?.subscriber?.plan.description}</p>
                            )}
                            <div className='flex gap-2'>
                                <p className='mb-0'>{policyDisplay(state.currentSubscription?.subscriber?.plan)}</p>
                                <p className='mb-0 account_name'>
                                    {amountDisplayPerPolicy(defMembership?.business?.currency, state.currentSubscription?.subscriber?.plan)}
                                </p>
                                <p className='mb-0 reveknew_red'>{formatBillingPeriodText(state.currentSubscription?.subscriber?.plan?.billingPeriod)}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="title flex">
                                <p className="labels">{t("plan.select_title")}</p>
                                <p className="isterik">*</p>
                            </div>
                            <TreeSelectComponent
                                ref={collectPaymentRef}
                                state={state}
                                setState={setState}
                                showClear={true}
                                showOneOffOnly={state.oneOffOnly}
                                placeholder={t('plan.select_title')}
                                className="w-full payments-activity-dropdown"
                            />
                            <div>
                                <Tooltip
                                    target=".collect-target-icon"
                                    className="planInfoTooltip"
                                >
                                    <div className="planInfo-div pb-0">
                                        {state.treePlanSelected?.description ? (
                                            <span className="planInfoDesc">
                                                {state.treePlanSelected?.description}
                                            </span>
                                        ) : null}

                                        {state.treePlanSelected?.policy ? (
                                            <div className="mb-1">
                                                <span className="text-left policy">
                                                    {state.treePlanSelected?.policy === "FIXED"
                                                        ? t("products.fixed_billing").toUpperCase()
                                                        : t('billing', { val: state.treePlanSelected?.policy })}
                                                </span>
                                            </div>
                                        ) : null}

                                        <div style={{ marginBottom: "0.5rem" }}>
                                            <div style={{ margin: "0rem 0.5rem 0rem 0rem" }}>
                                                <span className='label'>
                                                    {amountDisplayPerPolicy(defMembership?.business?.currency, state.treePlanSelected)}
                                                </span>
                                            </div>
                                            <div className="mb-0.5">
                                                <span className="planInfoBillingPeriod">
                                                    {formatBillingPeriodText(state.treePlanSelected?.billingPeriod)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Tooltip>

                                {state.treePlanSelected && (
                                    <div className="mt-2">
                                        <i
                                            className="collect-target-icon pi pi-info-circle"
                                            data-pr-position="bottom"
                                            style={{ fontSize: "12px", cursor: "pointer", color: "#495057", fontWeight: "600" }}
                                        />
                                        <span style={{ fontSize: "12px", color: "#495057", paddingLeft: "4px", fontWeight: "600" }}>
                                            {t("invitation.planDetailsInfo")}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {state.plan ? (
                        <div className="mt-3">
                            <div className="title flex">
                                <p className="labels">{t("business_customers.subscriber_name")}</p>
                                <p className="isterik">*</p>
                            </div>
                            <GlobalSearch
                                state={collectState}
                                setState={setCollectState}
                                defMembership={defMembership}
                                globalQuery={false}
                                globalBorderWidth={{ minWidth: "22rem", border: "none" }}
                            />
                        </div>
                    ) : (subscriberName && !state.onSubscriptionsTable) ? (
                        <div className="mt-3">
                            <div className="title flex">
                                <p className="labels">{t("business_customers.subscriber_name")}</p>
                            </div>
                            <InputText
                                style={{ height: "3rem" }}
                                value={subscriberName}
                                disabled
                            />
                        </div>
                    ) : null}

                    {showAmountField && (
                        <div>
                            <div className="title flex mt-4">
                                <p className="labels">{t("plan.enter_amount")}</p>
                                <p className="isterik">*</p>
                            </div>
                            <InputText
                                style={{ height: "3rem" }}
                                placeholder={t("plan.amount")}
                                value={inputAmount}
                                onChange={handleAmtChange}
                                maxLength={5}
                            />
                            {showErr && (
                                <p className='character_errors'>
                                    {t("plan.max_allowed", { currency: countryCurrency })}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="title flex mt-4">
                        <p className="labels">{t("recipient.MOBILE")}</p>
                        <p className="isterik">*</p>
                    </div>
                    <Dropdown
                        style={{ height: "3rem" }}
                        placeholder={t("recipient.select_type")}
                        value={systemBank}
                        onChange={(e) => setSystemBank(e.value)}
                        options={mobileBanks}
                        optionLabel="name"
                        dataKey="id"
                        disabled={fetchingLinkedBanks || !paymentProvider}
                        className='text-left items-center col-12 segment_drop'
                        id='segment_dialog'
                    />

                    <div className="invite-number mt-4">
                        <div className="title flex">
                            <p className="labels">{t("invitation.subscriber_number")}</p>
                            <p className="isterik">*</p>
                        </div>
                        <PhoneInput
                            name="number"
                            defaultCountry={defaultCountry}
                            value={phoneNumber || ''}
                            onChange={handlePhoneChange}
                            onBlur={handlePhoneBlur}
                            placeholder={t('invitation.number')}
                            inputClassName={classNames('phone-input', {
                                'p-invalid': phoneTouched && phoneError,
                            })}
                            className="phone-input-container"
                        />
                        {phoneTouched && phoneError && (
                            <div className="phone-error-message invitation_errors">
                                <span className="p-error">{phoneError}</span>
                            </div>
                        )}
                    </div>

                    <div className='flex justify-content-end mt-5'>
                        <Button
                            id={disableButtonConditions ? "disableButtonLook" : undefined}
                            style={{ width: "auto", backgroundColor: "#d0021b" }}
                            label={t("plan.collect_button")}
                            disabled={disableButtonConditions}
                            onClick={onSubmit}
                            loading={isTriggeringMomoPayment}
                        />
                    </div>
                </section>
            </Dialog>

            <Dialog
                visible={showWaitingModal}
                header={t("plan.momo_payment_waiting_title")}
                style={{ width: "350px" }}
                baseZIndex={1100}
                closable={false}
                onHide={() => {}}
            >
                <section className="p-3 flex flex-column align-items-center text-center gap-3">
                    <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                    <p className="mb-0 reveknew_gray">{t("plan.momo_payment_waiting_body")}</p>
                </section>
            </Dialog>
        </div>
    );
};

export default CollectPayment;
